/**
 * Admin Protected Route
 *
 * Requires authentication AND admin role.
 * Redirects to the main app login if not authenticated,
 * shows an access denied message if authenticated but not admin.
 */

import { useState, useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { authService, User } from '../services/auth.service';
import { Loader2, ShieldAlert } from 'lucide-react';

export const ProtectedRoute = () => {
  const location = useLocation();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(true);

  useEffect(() => {
    let isMounted = true;
    const fetchUser = async () => {
      try {
        const currentUser = await authService.getCurrentUser();
        if (isMounted) {
          setUser(currentUser);
          if (!currentUser) setIsAuthenticated(false);
        }
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error('Failed to fetch user in ProtectedRoute:', error);
        }
        if (isMounted) setIsAuthenticated(false);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchUser();
    return () => {
      isMounted = false;
    };
  }, [location.pathname]);

  // Not authenticated — redirect to main app login
  if (!isLoading && !isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
          <p className="text-sm text-muted-foreground font-medium font-body">Verifying admin access...</p>
        </div>
      </div>
    );
  }

  // Authenticated but not admin — show access denied.
  // ADMIN-PORTAL-AUDIT A5 (2026-05-25): accept both 'admin' and
  // 'superadmin' to match backend requireRole policy.
  if (user && user.role !== 'admin' && user.role !== 'superadmin') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="bg-card rounded-2xl shadow-elevated border border-border p-8 max-w-md w-full text-center">
          <div className="flex justify-center mb-6">
            <div className="flex items-center justify-center w-16 h-16 rounded-full bg-red-50 dark:bg-red-950/30">
              <ShieldAlert className="w-8 h-8 text-red-500" />
            </div>
          </div>
          <h1 className="text-2xl font-semibold font-heading text-foreground mb-2">
            Access Denied
          </h1>
          <p className="text-muted-foreground font-body leading-relaxed mb-6">
            This area is restricted to platform administrators. If you believe you should have access, please contact your system administrator.
          </p>
          <a
            href={import.meta.env.VITE_APP_URL || `${window.location.origin}/dashboard`}
            className="inline-flex items-center gap-2 bg-navy-800 hover:bg-navy-700 text-white px-6 py-2.5 rounded-xl font-medium transition-colors font-body text-sm"
          >
            Return to LexiReview
          </a>
        </div>
      </div>
    );
  }

  return <Outlet />;
};
